import { lazy, Suspense, type ReactNode } from "react"

import { SupplierContactsWrapper } from "~/pages/SupplierPortal/Contacts"
import { SupplierFAQWrapper } from "~/pages/SupplierPortal/FAQ"
import { SupplierProductionSitesWrapper } from "~/pages/SupplierPortal/ProductionSites"
import { SupplierPortalRouteFallback } from "~/pages/SupplierPortal/routes/SupplierPortalRouteFallback"
import { SubSupplierMonitoring } from "~/pages/SupplierPortal/SubSupplierMonitoring"

const LazyTraceabilityRequests = lazy(() =>
  import("~/pages/SupplierPortal/TraceabilityRequests").then((module) => ({
    default: module.TraceabilityRequests,
  }))
)

const LazySupplierMonitoringWrapper = lazy(() =>
  import("~/pages/Brand/Suppliers/List/SupplierMonitoringWrapper").then(
    (module) => ({
      default: module.SupplierMonitoringWrapper,
    })
  )
)

const LazySupplierProductionSites = lazy(() =>
  import("~/pages/SupplierPortal/ProductionSites/SupplierProductionSites").then(
    (module) => ({
      default: module.SupplierProductionSites,
    })
  )
)

const LazySupplierContacts = lazy(() =>
  import("~/pages/SupplierPortal/Contacts/SupplierContacts").then((module) => ({
    default: module.SupplierContacts,
  }))
)

const LazySupplierFAQ = lazy(() =>
  import("~/pages/SupplierPortal/FAQ/SupplierFAQ").then((module) => ({
    default: module.SupplierFAQ,
  }))
)

const LazySupplierOnboardingPage = lazy(() =>
  import("~/pages/SupplierPortal/Onboarding/SupplierOnboardingPage").then(
    (module) => ({
      default: module.SupplierOnboardingPage,
    })
  )
)

type SupplierMonitoringWrapperProps = Readonly<{
  type: "subSupplier"
}>

const SupplierPortalRouteSuspense = ({
  children,
}: Readonly<{ children: ReactNode }>) => (
  <Suspense fallback={<SupplierPortalRouteFallback />}>{children}</Suspense>
)

export const TraceabilityRequestsRoute = () => (
  <SupplierPortalRouteSuspense>
    <LazyTraceabilityRequests />
  </SupplierPortalRouteSuspense>
)

/** Thin layout shell — eager to avoid nested Suspense waterfall with leaf routes. */
export const SubSupplierMonitoringRoute = SubSupplierMonitoring

export const SupplierMonitoringRoute = ({
  type,
}: SupplierMonitoringWrapperProps) => (
  <SupplierPortalRouteSuspense>
    <LazySupplierMonitoringWrapper type={type} />
  </SupplierPortalRouteSuspense>
)

/** Thin layout shell — eager to avoid nested Suspense waterfall with leaf routes. */
export const SupplierProductionSitesWrapperRoute =
  SupplierProductionSitesWrapper

export const SupplierProductionSitesRoute = () => (
  <SupplierPortalRouteSuspense>
    <LazySupplierProductionSites />
  </SupplierPortalRouteSuspense>
)

/** Thin layout shell — eager to avoid nested Suspense waterfall with leaf routes. */
export const SupplierContactsWrapperRoute = SupplierContactsWrapper

export const SupplierContactsRoute = () => (
  <SupplierPortalRouteSuspense>
    <LazySupplierContacts />
  </SupplierPortalRouteSuspense>
)

/** Thin layout shell — eager to avoid nested Suspense waterfall with leaf routes. */
export const SupplierFAQWrapperRoute = SupplierFAQWrapper

export const SupplierFAQRoute = () => (
  <SupplierPortalRouteSuspense>
    <LazySupplierFAQ />
  </SupplierPortalRouteSuspense>
)

export const SupplierOnboardingPageRoute = () => (
  <SupplierPortalRouteSuspense>
    <LazySupplierOnboardingPage />
  </SupplierPortalRouteSuspense>
)
